import { useEffect, useSyncExternalStore } from 'react';

import { useChatDocumentViewer } from '@/hooks/useChatDocumentViewer';
import type { DocumentViewerOpenOptions } from '@/providers/ChatDocumentViewerContext';

/**
 * The Audio Overview bar lives in `App`, beside the router, so it sits
 * outside the document viewer's provider (mounted in `ProtectedLayout`). The
 * bridge below, rendered inside that provider, lends the bar its View
 * Document opener. Null while no provider is mounted (signed out): source
 * chips are hidden then.
 */
export type AudioOverviewLectureOpener = (
  lectureId: string,
  options?: DocumentViewerOpenOptions
) => void;

let opener: AudioOverviewLectureOpener | null = null;
const listeners = new Set<() => void>();

export const setAudioOverviewLectureOpener = (
  next: AudioOverviewLectureOpener | null
): void => {
  if (opener === next) return;
  opener = next;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getOpener = () => opener;

export const useAudioOverviewLectureOpener =
  (): AudioOverviewLectureOpener | null =>
    useSyncExternalStore(subscribe, getOpener, () => null);

/** Renders nothing; mount it inside `ChatDocumentViewerProvider`. */
export const AudioOverviewLectureOpenerBridge = () => {
  const { openLectureSession } = useChatDocumentViewer();

  useEffect(() => {
    setAudioOverviewLectureOpener(openLectureSession);
    return () => {
      // A newer bridge may have taken over already.
      if (opener === openLectureSession) setAudioOverviewLectureOpener(null);
    };
  }, [openLectureSession]);

  return null;
};
